import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Howl } from 'howler';
import TokenPurchasePanel from '../components/TokenPurchasePanel';
import LockerItemCard from '../components/LockerItemCard';
import FreeShippingProgress from '../components/FreeShippingProgress';
import PrizeRevealModal from '../components/PrizeRevealModal';
import TokenIcon from '../components/TokenIcon';
import Seo from '../shared/Seo';

// Machine style constants
const MACHINE_STYLE = {
  brandColor: '#C12127',
  accent: '#21476B',
  hopperCapsules: [
    ['#F5F8FF', '#F0463D'],
    ['#F5F8FF', '#2BA4B4'],
    ['#F5F8FF', '#F6C34E'],
    ['#F5F8FF', '#2BA4B4'],
    ['#F5F8FF', '#F0463D'],
    ['#F5F8FF', '#F6C34E'],
  ],
  dropCapsule: ['#F5F8FF', '#2BA4B4'],
};

const getBrandColor = (brand) => {
  if (!brand) {
    return MACHINE_STYLE.brandColor;
  }
  const normalizedBrand = brand.trim().toUpperCase();
  const BRAND_COLORS = {
    BANDAI: '#ee161f',
    'TAKARA TOMY': '#0e51a6',
    STASTO: '#f15928',
    BUSHIROAD: '#05923c',
  };
  return BRAND_COLORS[normalizedBrand] ?? MACHINE_STYLE.brandColor;
};

// Demo machine data
const DEMO_MACHINE = {
  id: 'demo-machine',
  name: 'Demo Machine',
  brand: 'PonPon!',
  tagline: 'Experience the magic',
  tokenCost: 3,
  imageUrl: '/assets/ponpon-fallback.svg', // Use ponpon fallback image
  inventoryAvailable: 100,
  inventoryTotal: 100,
  tags: ['Demo', 'PonPon!'],
  prizePool: [
    {
      id: 'demo-prize-1',
      name: 'Teddy Bear',
      image: '🧸',
      weight: 10,
      stock: 100,
      initialStock: 100,
    },
    {
      id: 'demo-prize-2',
      name: 'Collector Pin',
      imageUrl: '/assets/ponpon-logo.svg',
      weight: 10,
      stock: 100,
      initialStock: 100,
    },
    {
      id: 'demo-prize-3',
      name: 'Limited Edition Keychain',
      imageUrl: '/assets/ponpon-logo.svg',
      weight: 10,
      stock: 100,
      initialStock: 100,
    },
    {
      id: 'demo-prize-4',
      name: 'Art Print',
      imageUrl: '/assets/ponpon-logo.svg',
      weight: 10,
      stock: 100,
      initialStock: 100,
    },
    {
      id: 'demo-prize-5',
      name: 'Tote Bag',
      imageUrl: '/assets/ponpon-logo.svg',
      weight: 10,
      stock: 100,
      initialStock: 100,
    },
  ],
};

const TutorialPage = ({ isMobile = false }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1-5
  const [demoTokens] = useState(0); // Display only, never updated
  const [demoPrize, setDemoPrize] = useState(null);
  const [isPrizeModalOpen, setIsPrizeModalOpen] = useState(false);
  const [hasSkippedDemo, setHasSkippedDemo] = useState(false);
  const howToSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'PonPon! Locker walkthrough',
      description: 'See how to buy tokens, spin gachapon, store items in the Locker, and ship everything at once.',
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Buy tokens',
          text: 'Choose a token bundle and check out to start spinning online.',
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Spin a machine',
          text: 'Select a series, insert the required tokens, and crank the real machine remotely.',
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Use the Locker',
          text: 'Every item is stored in your Locker, where you can expand capacity and review pulls.',
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Ship everything',
          text: 'Once 8 slots (or more) are filled, request one free consolidated shipment.',
        },
      ],
    }),
    []
  );
  
  // Audio refs for Step 2 machine
  const spinSoundRef = useRef(null);
  const coinSoundRef = useRef(null);
  const heartbeatSoundRef = useRef(null);
  const revealSoundRef = useRef(null);
  const dropSoundRef = useRef(null);
  
  // Step 2 machine interaction states
  const [tokensInserted, setTokensInserted] = useState(0);
  const [activeCapsuleIndex] = useState(0); // Fixed index for demo, never updated
  const [capsuleOpened, setCapsuleOpened] = useState(false);
  const [dropComplete, setDropComplete] = useState(true);
  const [isAwaitingPrize, setIsAwaitingPrize] = useState(false);
  const [showPrizeChute, setShowPrizeChute] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [isDemoSpinning, setIsDemoSpinning] = useState(false);
  const [step2DemoPrize, setStep2DemoPrize] = useState(null);
  const [isStep2PrizeModalOpen, setIsStep2PrizeModalOpen] = useState(false);

  // Add pulse glow animation style
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse-glow {
        0%, 100% {
          box-shadow: 0 0 20px rgba(240, 70, 61, 0.4), 0 0 40px rgba(240, 70, 61, 0.2);
        }
        50% {
          box-shadow: 0 0 30px rgba(240, 70, 61, 0.6), 0 0 60px rgba(240, 70, 61, 0.4);
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Step 1: Welcome + Tokens intro
  // Step 2: Demo spin
  // Step 3: Locker explanation
  // Step 4: Shipping rules
  // Step 5: Email verification

  // Initialize audio for Step 2 machine
  useEffect(() => {
    try {
      spinSoundRef.current = new Howl({
        src: ['/audio/spinsound.mp3'],
        volume: 0.7,
        html5: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        spinSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      coinSoundRef.current = new Howl({
        src: ['/audio/coinsound.mp3'],
        volume: 0.6,
        html5: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        coinSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      heartbeatSoundRef.current = new Howl({
        src: ['/audio/heartbeatsound.mp3'],
        volume: 0.5,
        html5: true,
        loop: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        heartbeatSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      revealSoundRef.current = new Howl({
        src: ['/audio/revealsound.mp3'],
        volume: 0.7,
        html5: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        revealSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      dropSoundRef.current = new Howl({
        src: ['/audio/dropsound.mp3'],
        volume: 0.6,
        html5: true,
      });
    } catch (e) {
      // no-op if audio failed to init
    }
    return () => {
      try {
        dropSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  // Play heartbeat sound when capsule is jittering
  useEffect(() => {
    if (step !== 2) return;
    const shouldJitter = showPrizeChute && dropComplete && !capsuleOpened;
    try {
      const heartbeatSound = heartbeatSoundRef.current;
      if (heartbeatSound) {
        if (shouldJitter) {
          if (!heartbeatSound.playing()) {
            heartbeatSound.play();
          }
        } else {
          if (heartbeatSound.playing()) {
            heartbeatSound.stop();
          }
        }
      }
    } catch (e) {
      // no-op if audio failed to play/stop
    }
  }, [step, showPrizeChute, dropComplete, capsuleOpened]);

  const handleSkipDemo = useCallback(() => {
    setHasSkippedDemo(true);
    setStep(5); // Jump to email verification
  }, []);

  // Step 5: Redirect to homepage immediately
  useEffect(() => {
    if (step === 5) {
      navigate('/', { replace: true });
    }
  }, [step, navigate]);

  // Step 2: Machine interaction logic
  const hopperCapsules = React.useMemo(() => MACHINE_STYLE.hopperCapsules, []);
  const dropCapsule = React.useMemo(() => MACHINE_STYLE.dropCapsule, []);
  const capsuleStyle = React.useMemo(
    () => hopperCapsules[activeCapsuleIndex] ?? dropCapsule,
    [activeCapsuleIndex, hopperCapsules, dropCapsule]
  );

  const isFullyInserted = tokensInserted >= DEMO_MACHINE.tokenCost;
  const knobDisabled = !isFullyInserted;
  const coinSlotDisabled = isDemoSpinning || isFullyInserted || !dropComplete;
  const effectiveIsSpinning = isDemoSpinning;
  const effectivePrize = step2DemoPrize;

  const knobAxisClassNames = ['machine-knob-axis'];
  if (effectiveIsSpinning) {
    knobAxisClassNames.push('is-rotating');
  }
  const knobButtonClassNames = ['machine-knob'];
  if (knobDisabled || effectiveIsSpinning) {
    knobButtonClassNames.push('is-disabled');
  }
  if (isFullyInserted && !effectiveIsSpinning) {
    knobButtonClassNames.push('is-armed');
  }

  const coinSlotClassNames = ['machine-coin-slot'];
  if (coinSlotDisabled) {
    coinSlotClassNames.push('is-disabled');
  }
  if (isFullyInserted) {
    coinSlotClassNames.push('token-inserted');
  }
  const hasCapsuleWaiting = showPrizeChute && dropComplete && !capsuleOpened;
  if (!coinSlotDisabled && !isFullyInserted && !hasCapsuleWaiting) {
    coinSlotClassNames.push('nudge-shake');
  }

  const handleInsertToken = React.useCallback(() => {
    if (isDemoSpinning || !dropComplete) {
      return;
    }
    const tokensRemaining = DEMO_MACHINE.tokenCost - tokensInserted;
    if (tokensRemaining <= 0) {
      return;
    }
    setCapsuleOpened(false);
    setTokensInserted(tokensInserted + 1);
    
    // Play coin sound
    try {
      const coinSound = coinSoundRef.current;
      if (coinSound) {
        if (coinSound.playing()) {
          coinSound.stop();
        }
        coinSound.play();
      }
    } catch (e) {
      // no-op if audio failed to play
    }
  }, [tokensInserted, isDemoSpinning, dropComplete]);

  const handleKnobClick = React.useCallback(() => {
    if (knobDisabled || effectiveIsSpinning) {
      return;
    }
    if (!isFullyInserted) {
      return;
    }
    
    // Play spin sound
    try {
      const s = spinSoundRef.current;
      if (s) {
        if (s.playing()) {
          s.stop();
        }
        s.play();
      }
    } catch {}
    
    setTokensInserted(0);
    setCapsuleOpened(false);
    setIsAwaitingPrize(false);
    setShowPrizeChute(false);
    setDropComplete(false);
    setIsDemoSpinning(true);
    
    // Play drop sound after delay
    setTimeout(() => {
      try {
        const dropSound = dropSoundRef.current;
        if (dropSound) {
          if (dropSound.playing()) {
            dropSound.stop();
          }
          dropSound.play();
        }
      } catch (e) {
        // no-op if audio failed to play
      }
    }, 1100);
  }, [knobDisabled, effectiveIsSpinning, isFullyInserted]);

  const handleDropAnimationEnd = React.useCallback(() => {
    setDropComplete(true);
    setIsAwaitingPrize(true);
    setIsDemoSpinning(false);
    // After drop completes, show prize chute
    setShowPrizeChute(true);
  }, []);

  const handleCapsuleOpen = React.useCallback(() => {
    if (capsuleOpened) {
      return;
    }
    
    // Set a demo prize when capsule is clicked (before opening)
    if (!step2DemoPrize) {
      const demoPrizeData = {
        id: 'demo-prize-' + Date.now(),
        name: DEMO_MACHINE.prizePool?.[0]?.name || 'Teddy Bear',
        image: DEMO_MACHINE.prizePool?.[0]?.image || '🧸',
      };
      setStep2DemoPrize(demoPrizeData);
    }
    
    // Stop heartbeat sound
    try {
      heartbeatSoundRef.current?.stop();
    } catch (e) {
      // no-op if audio failed to stop
    }
    
    // Play reveal sound
    try {
      const revealSound = revealSoundRef.current;
      if (revealSound) {
        if (revealSound.playing()) {
          revealSound.stop();
        }
        revealSound.play();
      }
    } catch (e) {
      // no-op if audio failed to play
    }
    
    setCapsuleOpened(true);
    setShowFireworks(true);
    setTimeout(() => setShowFireworks(false), 1300);
    
    // Show PrizeRevealModal after reveal animation completes
    const invokeReveal = () => {
      setIsStep2PrizeModalOpen(true);
    };
    setTimeout(() => {
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(invokeReveal);
      } else {
        invokeReveal();
      }
    }, 950);
  }, [capsuleOpened, step2DemoPrize]);

  // Reset machine state when entering Step 2
  React.useEffect(() => {
    if (step === 2) {
      setTokensInserted(0);
      setCapsuleOpened(false);
      setDropComplete(true);
      setIsAwaitingPrize(false);
      setShowPrizeChute(false);
      setShowFireworks(false);
      setIsDemoSpinning(false);
      setStep2DemoPrize(null);
      setIsStep2PrizeModalOpen(false);
    }
  }, [step]);

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="flex flex-col gap-6 text-center">
            <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
              Welcome to the PonPon Tutorial
            </h1>
            <p className="text-lg text-ponpon-navy/70">
              Choose a token bundle to start!
            </p>
            <div className="mt-4 flex flex-col items-center gap-4">
              {/* Token button that opens the panel */}
              <div className="relative w-full flex flex-col items-center">
                <button
                  type="button"
                  className="flex h-9 items-center gap-2 rounded-full border border-white/50 bg-white/90 px-4 text-sm font-semibold text-ponpon-navy shadow-sm transition hover:border-ponpon-coral/50 hover:text-ponpon-coral"
                  style={{
                    animation: 'pulse-glow 2s ease-in-out infinite',
                  }}
                >
                  <TokenIcon className="w-4 h-4" alt="tokens" />
                  <span className="font-semibold">{demoTokens}</span>
                  <span className="text-xs font-medium leading-none text-ponpon-navy/70">
                    Tokens
                  </span>
                </button>
                {/* Panel appears below */}
                <div className="relative mt-4 w-full flex justify-center">
                  <TokenPurchasePanel demoMode={true} onBundleSelect={handleBundleSelect} />
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={handleSkipDemo}
                className="text-sm text-ponpon-navy/60 underline hover:text-ponpon-coral transition-colors"
              >
                Skip Tutorial
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="flex flex-col items-center justify-center w-full min-h-[60vh]">
            <div className="demo-machine-wrapper w-full max-w-xl mx-auto flex items-center justify-center">
              <div className="machine-unit machine-unit-large relative mx-auto" style={{ width: '100%' }}>
              <div className="machine-top" style={{ borderColor: `${MACHINE_STYLE.accent}33` }}>
                <div className="machine-brand" style={{ color: getBrandColor(DEMO_MACHINE.brand) }}>
                  {DEMO_MACHINE.brand || 'PonPon'}
                </div>
              </div>
              <div className="machine-glass">
                {DEMO_MACHINE.imageUrl ? (
                  <div className="machine-primary-image flex h-full w-full items-center justify-center bg-white/70">
                    <img
                      src={DEMO_MACHINE.imageUrl}
                      alt={DEMO_MACHINE.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="machine-primary-image flex h-full w-full items-center justify-center bg-white/70">
                    <div className="text-6xl">🎁</div>
                  </div>
                )}
              </div>
              <div className="machine-body-shell">
                <button
                  type="button"
                  onClick={handleInsertToken}
                  disabled={coinSlotDisabled}
                  className="machine-insert-panel relative"
                  aria-label={
                    isFullyInserted
                      ? `All ${DEMO_MACHINE.tokenCost} token${DEMO_MACHINE.tokenCost === 1 ? '' : 's'} inserted`
                      : `Insert token ${tokensInserted + 1} of ${DEMO_MACHINE.tokenCost}`
                  }
                  aria-pressed={isFullyInserted}
                >
                  <div className="machine-label" style={{ color: MACHINE_STYLE.accent }}>
                    Insert Token
                  </div>
                  <div className={coinSlotClassNames.join(' ')}>
                    {!isFullyInserted && (
                      <span className="machine-coin-icon" aria-hidden="true">
                        <TokenIcon className="w-4 h-4" alt="" />
                      </span>
                    )}
                    <span className="machine-token-text">
                      {isFullyInserted ? (
                        <strong>Tap →</strong>
                      ) : (
                        <strong>{tokensInserted}/{DEMO_MACHINE.tokenCost}</strong>
                      )}
                    </span>
                  </div>
                  <div className="machine-slot-rails" aria-hidden="true">
                    <span className="slot-rail slot-rail-left" />
                    <span className="slot-rail slot-rail-right" />
                  </div>
                </button>
                <div className={knobAxisClassNames.join(' ')}>
                  <button
                    type="button"
                    onClick={handleKnobClick}
                    disabled={knobDisabled || effectiveIsSpinning}
                    className={knobButtonClassNames.join(' ')}
                    aria-label="Turn dial to spin the machine"
                  >
                    <span className="machine-knob-face" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="machine-chute" aria-hidden="true">
                {(!dropComplete || (dropComplete && !showPrizeChute && isAwaitingPrize)) && (
                  <div
                    className="active-drop-capsule"
                    style={{ '--capsule-top': capsuleStyle[0], '--capsule-bottom': capsuleStyle[1] }}
                    onAnimationEnd={handleDropAnimationEnd}
                  >
                    <div className="active-drop-shell" />
                  </div>
                )}
                {showPrizeChute && dropComplete && (!effectiveIsSpinning || isAwaitingPrize) ? (
                  <div
                    className={`chute-ready ${capsuleOpened ? 'opened' : ''} relative`}
                    style={{ '--capsule-top': capsuleStyle[0], '--capsule-bottom': capsuleStyle[1] }}
                  >
                    <button
                      type="button"
                      onClick={handleCapsuleOpen}
                      disabled={capsuleOpened || !showPrizeChute}
                      className={`chute-capsule-trigger ${capsuleOpened ? 'opened' : ''}`}
                    >
                      <div
                        className={`chute-capsule-shell ${capsuleOpened ? 'open' : ''} ${
                          !capsuleOpened && showPrizeChute ? 'capsule-jitter' : ''
                        }`}
                      />
                    </button>
                    {capsuleOpened && effectivePrize ? (
                      <div className="chute-prize-display reveal">
                        {typeof effectivePrize?.imageUrl === 'string' && effectivePrize.imageUrl.trim().length > 0 ? (
                          <img
                            src={effectivePrize.imageUrl}
                            alt={effectivePrize.name || 'Item'}
                            className="max-h-24 max-w-[70%] object-contain"
                          />
                        ) : (
                          <span className="text-4xl sm:text-5xl">{effectivePrize?.image || '🎁'}</span>
                        )}
                      </div>
                    ) : null}
                    {showFireworks ? (
                      <div className="fireworks" aria-hidden="true">
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  // CHANGED: Always render empty chute area to maintain 5:3:2 ratio
                  <div className="chute-ready relative" style={{ '--capsule-top': capsuleStyle[0], '--capsule-bottom': capsuleStyle[1] }}>
                    <div className="chute-capsule-shell" style={{ opacity: 0 }} />
                  </div>
                )}
              </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="flex flex-col gap-6">
            <h2 className="font-display text-2xl font-semibold text-ponpon-navy text-center flex items-center justify-center gap-2">
              <img
                src="/assets/locker-icon.png"
                alt="Locker"
                className="w-6 h-6 object-contain flex-shrink-0"
                aria-hidden="true"
              />
              Your Locker can store up to 8 items
            </h2>
            <div className="grid grid-cols-4 gap-2 sm:gap-3">
              <div className="aspect-square">
                <LockerItemCard 
                  entry={{
                    name: demoPrize?.name,
                    imageUrl: demoPrize?.imageUrl,
                    prize: demoPrize,
                  }} 
                  showTimestamp={false}
                  showText={false}
                />
              </div>
              {Array.from({ length: 7 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square flex items-center justify-center rounded-3xl border border-dashed border-ponpon-navy/20 bg-white/50 p-2 sm:p-5"
                >
                  <span className="text-xl sm:text-2xl">📦</span>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-ponpon-navy/60">
              Items automatically go into your Locker
            </p>
            <button
              type="button"
              onClick={() => setStep(4)}
              className="inline-flex items-center justify-center rounded-full bg-ponpon-coral px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
            >
              Let's ship it out 🚀
            </button>
          </div>
        );

      case 4:
        return (
          <div className="flex flex-col gap-6">
            <h2 className="font-display text-2xl font-semibold text-ponpon-navy text-center">
              Shipping Rules
              <span className="block text-sm font-normal text-ponpon-navy/60">(US only)</span>
            </h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="rounded-3xl border border-orange-500/30 bg-orange-50 px-6 py-8">
                <FreeShippingProgress currentCount={3} />
                <div className="mt-8 text-center">
                  <p className="text-base font-semibold text-orange-700 mb-2">
                    📦 Partial Shipment
                  </p>
                  <p className="text-sm text-orange-600/80 mb-2">
                    (less than 8 items)
                  </p>
                  <p className="text-lg font-bold text-orange-800">
                    → $6.99 standard shipping
                  </p>
                </div>
              </div>
              <div className="rounded-3xl border border-green-500/30 bg-green-50 px-6 py-8">
                <FreeShippingProgress currentCount={8} />
                <div className="mt-8 text-center">
                  <p className="text-base font-semibold text-green-700 mb-2">
                    ✅ 8 items
                  </p>
                  <p className="text-lg font-bold text-green-800">
                    → Free shipping
                  </p>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setStep(5)}
              className="inline-flex items-center justify-center rounded-full bg-ponpon-coral px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
            >
              Got it! Try your first spin!
            </button>
          </div>
        );

      case 5:
        return null; // Redirect to homepage immediately

      default:
        return null;
    }
  };

  const handleBundleSelect = useCallback(() => {
    if (step === 1) {
      // CHANGED: Go to Step 2 (blank page) - stay here for development
      setStep(2);
      // Set a demo prize for Step 3 display
      setDemoPrize({
        id: 'demo-prize',
        name: 'Teddy Bear',
        image: '🧸',
      });
    }
  }, [step]);

  const handleBack = useCallback(() => {
    if (step > 1) {
      setStep(step - 1);
    }
  }, [step]);

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <Seo
        title="Tutorial | PonPon!"
        description="Master PonPon! in a few steps—buy tokens, spin real gachapon, manage your Locker, and request one free shipment."
        canonical="https://ponpon.app/tutorial"
        jsonLd={howToSchema}
      />
      {/* Back button - show from step 2 onwards */}
      {step >= 2 && (
        <div className="fixed top-4 left-4 z-[70]">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-2 rounded-full border border-white/50 bg-white/90 px-4 py-2 text-sm font-semibold text-ponpon-navy/70 shadow-sm transition hover:-translate-y-0.5 hover:border-ponpon-coral/40 hover:text-ponpon-coral focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/50"
          >
            <span aria-hidden="true">←</span>
            Back
          </button>
        </div>
      )}
      
      <div className="w-full max-w-2xl">
        {/* Step indicator */}
        {!hasSkippedDemo && (
          <div className="mb-8 flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`h-2 w-2 rounded-full transition-colors ${
                  s <= step ? 'bg-ponpon-coral' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        )}
        
        {/* Step content */}
        <div className="rounded-3xl border border-white/70 bg-white/95 p-4 shadow-lg">
          {renderStepContent()}
        </div>
      </div>

      {/* Prize reveal modal for Step 3+ */}
      {demoPrize && step !== 2 && (
        <PrizeRevealModal
          isOpen={isPrizeModalOpen}
          prize={demoPrize}
          onClose={() => setIsPrizeModalOpen(false)}
          onViewLocker={() => {
            setIsPrizeModalOpen(false);
            setStep(3);
          }}
          demoMode={true}
        />
      )}
      
      {/* Prize reveal modal for Step 2 */}
      {step2DemoPrize && step === 2 && (
        <PrizeRevealModal
          isOpen={isStep2PrizeModalOpen}
          prize={step2DemoPrize}
          onClose={() => {
            setIsStep2PrizeModalOpen(false);
            // After closing modal, advance to Step 3
            setTimeout(() => {
              setDemoPrize(step2DemoPrize);
              setStep(3);
            }, 300);
          }}
          onViewLocker={() => {
            setIsStep2PrizeModalOpen(false);
            setDemoPrize(step2DemoPrize);
            setStep(3);
          }}
          demoMode={true}
        />
      )}
    </div>
  );
};

export default TutorialPage;
